import { useCallback, useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { PORTFOLIO, PORTFOLIO_CATEGORIES } from "@/data/content";
import { PORTFOLIO as PID } from "@/constants/testIds";
import Seo from "@/components/site/Seo";

const FILTERS = [{ id: "all", label: "All Work" }, ...PORTFOLIO_CATEGORIES];

// Deterministic masonry — assigns rowspan based on a hash so the grid stays varied.
function masonrySpan(id) {
  const h = id.split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  return h % 3 === 0 ? "row-span-2" : "";
}

export default function Portfolio() {
  const [filter, setFilter] = useState("all");
  const [lightbox, setLightbox] = useState(null);

  const items = useMemo(
    () => (filter === "all" ? PORTFOLIO : PORTFOLIO.filter((p) => p.category === filter)),
    [filter]
  );

  const close = useCallback(() => setLightbox(null), []);
  const next = useCallback(
    () => setLightbox((i) => (i === null ? null : (i + 1) % items.length)),
    [items.length]
  );
  const prev = useCallback(
    () => setLightbox((i) => (i === null ? null : (i - 1 + items.length) % items.length)),
    [items.length]
  );

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [lightbox, close, next, prev]);

  const categoryLabel = (id) =>
    PORTFOLIO_CATEGORIES.find((c) => c.id === id)?.label || id;

  return (
    <div className="bg-[#111111] text-[#FDFBF6] min-h-screen">
      <Seo
        title="Event Catering Portfolio — Weddings, Corporate Events, Food Festivals | Indian Alchemy"
        description="Photography from luxury weddings, cocktail evenings, high teas, corporate cafeterias and food festivals catered by Indian Alchemy × Platinum Hospitality across Gurugram, Delhi NCR & destination venues."
        path="/portfolio"
        keywords="wedding catering portfolio Gurugram, corporate event catering Delhi NCR, food festival caterer Gurgaon, luxury event photography Indian Alchemy"
      />
      {/* HEADER */}
      <section className="pt-32 md:pt-40 pb-12">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961]">
            Portfolio · 2023 / 2025
          </div>
          <h1 className="mt-6 font-serif font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.02] max-w-4xl">
            Rooms we have quietly <em className="italic text-[#C9A961]">cooked in</em>.
          </h1>
          <p className="mt-6 max-w-2xl text-sm text-[#FDFBF6]/60 leading-relaxed">
            Photography, not paragraphs. Tap any frame to open.
          </p>

          {/* Gold tabs */}
          <div className="mt-12 flex flex-wrap gap-x-6 gap-y-3 border-b border-[#C9A961]/25 pb-1">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                data-testid={PID.filter(f.id)}
                onClick={() => setFilter(f.id)}
                className={`relative text-[11px] uppercase tracking-[0.24em] pb-4 transition-colors ${
                  filter === f.id
                    ? "text-[#C9A961]"
                    : "text-[#FDFBF6]/60 hover:text-[#C9A961]"
                }`}
              >
                {f.label}
                {filter === f.id && (
                  <motion.span
                    layoutId="portfolio-tab-underline"
                    className="absolute left-0 right-0 -bottom-[1px] h-[2px] bg-[#C9A961]"
                  />
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* MASONRY GRID */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <motion.div
            layout
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 auto-rows-[220px] md:auto-rows-[240px] lg:auto-rows-[260px] gap-2 md:gap-3"
          >
            <AnimatePresence mode="popLayout">
              {items.map((item, i) => (
                <motion.button
                  key={item.id}
                  data-testid={PID.item(item.id)}
                  layout
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 10 }}
                  transition={{ delay: i * 0.04, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  onClick={() => setLightbox(i)}
                  className={`group relative overflow-hidden text-left ${masonrySpan(item.id)}`}
                >
                  <img
                    src={item.src}
                    alt={`${item.title} — ${item.category} catering by Indian Alchemy, ${item.location}`}
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.06]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#111]/85 via-transparent to-transparent opacity-80 group-hover:opacity-100 transition-opacity" />
                  <div className="absolute inset-0 p-4 md:p-5 flex flex-col justify-end">
                    <div className="text-[10px] tracking-[0.28em] uppercase text-[#C9A961]/90">
                      {categoryLabel(item.category)} · {item.location}
                    </div>
                  </div>
                </motion.button>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* LIGHTBOX */}
      <AnimatePresence>
        {lightbox !== null && items[lightbox] && (
          <motion.div
            key="lb"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-[#0A0A0A]/97 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
            onClick={close}
          >
            <button
              className="absolute top-5 right-5 md:top-8 md:right-10 h-11 w-11 rounded-full border border-[#C9A961]/40 text-[#C9A961] flex items-center justify-center hover:bg-[#C9A961]/10"
              onClick={close}
              data-testid={PID.lightboxClose}
              aria-label="Close"
            >
              <X size={18} />
            </button>
            <button
              className="absolute left-5 md:left-10 h-11 w-11 rounded-full border border-[#C9A961]/40 text-[#C9A961] flex items-center justify-center hover:bg-[#C9A961]/10"
              onClick={(e) => { e.stopPropagation(); prev(); }}
              data-testid={PID.lightboxPrev}
              aria-label="Previous"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              className="absolute right-5 md:right-10 h-11 w-11 rounded-full border border-[#C9A961]/40 text-[#C9A961] flex items-center justify-center hover:bg-[#C9A961]/10"
              onClick={(e) => { e.stopPropagation(); next(); }}
              data-testid={PID.lightboxNext}
              aria-label="Next"
            >
              <ChevronRight size={20} />
            </button>

            <motion.figure
              key={items[lightbox].id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              transition={{ duration: 0.4 }}
              className="max-w-5xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={items[lightbox].src}
                alt={`${items[lightbox].title} — ${items[lightbox].category} by Indian Alchemy in ${items[lightbox].location}`}
                className="w-full max-h-[78vh] object-contain"
              />
              <figcaption className="mt-5 flex items-center justify-between text-[#FDFBF6]/85">
                <div className="text-[10px] tracking-[0.32em] uppercase text-[#C9A961]">
                  {categoryLabel(items[lightbox].category)} · {items[lightbox].location}
                </div>
                <div className="text-xs opacity-70">
                  {lightbox + 1} / {items.length}
                </div>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
